// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Point, Rectangle } from '@shared/commons/Geometry';
import $ from 'jquery';
import { BOTTOM_WINDOWS_MARGIN } from '../UtilsClasses/SizeConstants';
// #region Capture
class Capture {
    constructor() {
        this.capturedObject = null;
        this.offsetLeft = 0;
        this.offsetTop = 0;
        this.box = {
            xMin: 0,
            xMax: 0,
            yMin: 0,
            yMax: 0
        };
    }
    SetCapture(sender, event) {
        if (!isNullOrUndefined(this.capturedObject)) {
            Capture.onDocumentMouseUp(event);
            return;
        }
        sender.captured = true;
        this.capturedObject = sender;
        try {
            this.offsetLeft = event.pageX - event.offsetX;
            this.offsetTop = event.pageY - event.offsetY;
            const curElement = $(event.target);
            const h = curElement.innerHeight();
            const w = curElement.innerWidth();
            const lt = curElement.offset();
            this.box = {
                xMin: lt.left,
                xMax: lt.left + w,
                yMin: lt.top,
                yMax: lt.top + h
            };
            const $doc = $(document);
            $doc.on('mousemove', Capture.onDocumentMouseMove);
            $doc.on('mouseup', Capture.onDocumentMouseUp);
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
    }
    ClearCapture() {
        const $doc = $(document);
        $doc.off('mousemove', Capture.onDocumentMouseMove);
        $doc.off('mouseup', Capture.onDocumentMouseUp);
        if (!isNullOrUndefined(this.capturedObject)) {
            delete this.capturedObject.captured;
            delete this.capturedObject;
        }
        this.offsetLeft = 0;
        this.offsetTop = 0;
        this.box = {
            xMin: 0,
            xMax: 0,
            yMin: 0,
            yMax: 0
        };
    }
    static onDocumentMouseMove(event) {
        const capture = ControlsUtils.Capture;
        const tmpX = event.pageX - capture.offsetLeft;
        const tmpY = event.pageY - capture.offsetTop;
        if (event.pageX > capture.box.xMin) {
            event.offsetX = tmpX;
        }
        else {
            event.offsetX = capture.box.xMin;
        }
        if (event.pageX < capture.box.xMax) {
            event.offsetX = tmpX;
        }
        else {
            event.offsetX = capture.box.xMax;
        }
        const minY = capture.box.yMin;
        if (tmpY > minY) {
            event.offsetY = tmpY;
        }
        else {
            event.offsetY = minY;
        }
        const maxY = capture.box.yMax;
        if (tmpY < maxY) {
            event.offsetY = tmpY;
        }
        else {
            event.offsetY = maxY;
        }
        capture.capturedObject.onMouseMove(event);
    }
    static onDocumentMouseUp(event) {
        const capture = ControlsUtils.Capture;
        if (isNullOrUndefined(event.originalEvent)) {
            event = $.Event(event);
        }
        event.offsetX = event.pageX - capture.offsetLeft;
        event.offsetY = event.pageY - capture.offsetTop;
        if (!isNullOrUndefined(capture.capturedObject)) {
            capture.capturedObject.onMouseUp(event);
        }
        capture.ClearCapture();
    }
}
// #endregion Capture
export var MouseButtons;
(function (MouseButtons) {
    MouseButtons[MouseButtons["Left"] = 0] = "Left";
    MouseButtons[MouseButtons["Middle"] = 1] = "Middle";
    MouseButtons[MouseButtons["Right"] = 2] = "Right";
})(MouseButtons || (MouseButtons = {}));
class _ControlsUtils {
    constructor() {
        this.Capture = new Capture();
        this.canvas = null;
    }
    getAbsoluteLocation(control) {
        const $window = $(window);
        const $control = $(control.find('*'));
        const offset = $control.offset();
        const posX = offset.left - $window.scrollLeft();
        const posY = offset.top - $window.scrollTop();
        return new Point(posX, posY);
    }
    GetTextWidth(text, font) {
        // CanvasRenderingContext2D.prototype.GetTextWidth ?
        const canvas = this.canvas || (this.canvas = document.createElement('canvas')); // re-use canvas object for better performance
        const context = canvas.getContext('2d');
        context.font = font;
        const metrics = context.measureText(text);
        return metrics.width;
    }
    // #region SVG circle
    polarToCartesian(centerX, centerY, radius, angleInDegrees) {
        const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
        return new Point(centerX + radius * Math.cos(angleInRadians), centerY + radius * Math.sin(angleInRadians));
    }
    getRactiveFromHTMLElement(el) {
        var _a, _b;
        return (_b = (_a = el['_ractive']) === null || _a === void 0 ? void 0 : _a.proxy) === null || _b === void 0 ? void 0 : _b.ractive;
    }
    getAbsoluteRectangleForElement(element) {
        const $root = $(element.find('div'));
        const $window = $(window);
        const offset = $root.offset();
        return new Rectangle(offset.left - $window.scrollLeft(), offset.top - $window.scrollTop(), $root.width(), $root.height());
    }
    adjustPositionToFitWindow(x, y, width, height) {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        if (x + width > windowWidth) {
            x = windowWidth - width;
        }
        if (x < 0) {
            x = 0;
        }
        if (y + height > windowHeight) {
            y = windowHeight - height;
        }
        if (y < 0) {
            y = 0;
        }
        return { x, y };
    }
    centerPosition(width, height) {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const x = (windowWidth - width) / 2;
        const y = (windowHeight - height) / 2;
        return { x, y };
    }
    CorrectPopupLocation(bounds, parent) {
        let X = bounds.X;
        let Y = bounds.Y;
        const screenB = window.innerHeight - BOTTOM_WINDOWS_MARGIN;
        let screenR = window.innerWidth;
        const screenRect = new Rectangle(0, 0, screenR, screenB);
        let bR = bounds.X + bounds.Width;
        let bB = bounds.Y + bounds.Height;
        if (!screenRect.Contains(bR, bB)) {
            // need to move our rectangle
            if (bR > screenR) {
                X -= bR - screenR;
                if (!isNullOrUndefined(parent)) {
                    screenR = parent.get('left');
                }
            }
            if (bounds.X < 0) {
                X = 0;
            }
            if (bB > screenB) {
                Y -= bB - screenB;
            }
            if (Y < 0) {
                Y = 0;
            }
        }
        if (!isNullOrUndefined(parent)) {
            bR = X + bounds.Width;
            bB = Y + bounds.Height;
            // need to move our rectangle
            if (bR > screenR) {
                X -= bR - screenR;
            }
            if (bounds.X < 0) {
                X = 0;
            }
            if (bB > screenB) {
                Y -= bB - screenB;
            }
            if (Y < 0) {
                Y = 0;
            }
        }
        return { newX: X, newY: Y };
    }
}
export const ControlsUtils = new _ControlsUtils();
